import React, { useMemo, useState } from "react";
import { FileText, FileCheck2, MessageCircle, ClipboardList, X } from "lucide-react";
import "./ApplicationReviewModal.css";

const norm = (v) => (v || "").toString().trim().toLowerCase();

function statusLabel(raw, locale = "kz") {
  const s = norm(raw);

  const labels = {
    "қателерді анықтау": { kz: "Қателерді анықтау", ru: "Выявление ошибок" },
    "қате анықтау аяқталды": { kz: "Қате анықтау аяқталды", ru: "Выявление ошибок завершено" },
    "бағалау": { kz: "Бағалау", ru: "Оценка" },
    "күтуде": { kz: "Күтуде", ru: "В ожидании" },
    "аяқталды": { kz: "Аяқталды", ru: "Завершено" },
    "бекітілді": { kz: "Бекітілді", ru: "Утверждено" },
  };

  return labels[s]?.[locale] || raw || "—";
}

function getTone(raw) {
  const s = norm(raw);
  if (["аяқталды", "бекітілді", "қате анықтау аяқталды"].includes(s)) return "success";
  if (["қайтарылды", "отклонён", "отклонен"].includes(s)) return "danger";
  if (["бағалау", "өңдеуде"].includes(s)) return "info";
  return "warning";
}

function isLeadExpert(exp) {
  return (
    Boolean(exp?.is_primary_scientist) ||
    Number(exp?.is_primary_scientist) === 1 ||
    Boolean(exp?.is_main) ||
    Boolean(exp?.is_lead) ||
    Boolean(exp?.is_head) ||
    Boolean(exp?.main_expert) ||
    Boolean(exp?.lead_expert)
  );
}

function getName(exp) {
  return (
    exp?.name || exp?.full_name || exp?.expert_name ||
    [exp?.last_name, exp?.first_name, exp?.patronymic].filter(Boolean).join(" ") ||
    "—"
  );
}

function getRole(exp, locale = "kz") {
  const isKz = locale === "kz";
  const degree = String(exp?.academic_degree || "").toLowerCase();
  if (degree.includes("алым") || degree.includes("учён") || degree.includes("наук")) 
    return isKz ? "Ғалым" : "Учёный";
  if (degree.includes("педаг")) 
    return isKz ? "Педагог" : "Педагог";
  if (degree.includes("метод")) 
    return isKz ? "Әдіскер" : "Методист";
  return isKz ? "Сарапшы" : "Эксперт";
}

function getSubject(exp, locale = "kz") {
  if (!exp?.subject) return "—";
  const map = {
    "Казахский язык": "Қазақ тілі",
    "Русский язык": "Орыс тілі",
    "Казахская литература": "Қазақ әдебиеті",
    "Русская литература": "Орыс әдебиеті",
  };
  return locale === "kz" ? map[exp.subject] || exp.subject : exp.subject;
}

function getScore(exp, locale = "kz") {
  const score = exp?.score ?? exp?.average_score ?? exp?.avg_score;
  if (score === null || score === undefined || score === "") {
    return locale === "kz" ? "Қойылмаған" : "Не выставлен";
  }
  return score;
}

function formatDateTime(v) {
  if (!v) return "—";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
}

/* ── inline style for all white icons inside dark buttons ── */
const WHITE_ICON = { color: "#ffffff", stroke: "#ffffff" };

export default function ApplicationReviewModal(props) {
  const {
    isOpen, application, experts = [], locale = "kz",
    finalAverage, summaryDecision, trExpertStatus, trVerdict,
    onClose, onOpenChat, onOpenErrorReview,
    downloadMistakesFile, downloadDecisionFile,
    promoteToEvaluation, finalDocuments = [],
    downloadFinalDecision, downloadFinalDoc, downloadFinalAttachment,
    showUploadModal, setShowUploadModal,
    finalFile, setFinalFile, finalUploaded, setFinalUploaded,
    uploadFinalAttachment, handleSendAndGenerate, sendLoading,
    ChatBoxComponent, chatBoxProps,
    refreshApplication, refetch, onResendSuccess,
  } = props;

  const isKz = locale === "kz";
  const [resendLoadingId, setResendLoadingId] = useState(null);

  const sortedExperts = useMemo(() => {
    return [...(Array.isArray(experts) ? experts : [])].sort((a, b) => {
      const aLead = isLeadExpert(a) ? 1 : 0;
      const bLead = isLeadExpert(b) ? 1 : 0;
      if (aLead !== bLead) return bLead - aLead;
      return getName(a).localeCompare(getName(b), locale);
    });
  }, [experts, locale]);

  const leadExpert = sortedExperts.find(isLeadExpert);
  const completedCount = sortedExperts.filter((e) =>
    ["аяқталды", "бекітілді", "қате анықтау аяқталды"].includes(norm(e?.status))
  ).length;

  if (!isOpen || !application) return null;

  const safeStatus = (v) =>
    typeof trExpertStatus === "function" ? trExpertStatus(v) : statusLabel(v, locale);

  const safeVerdict = (v) =>
    typeof trVerdict === "function" ? trVerdict(v) : v ?? "—";

  const allErrorDetectionFinished =
    sortedExperts.length > 0 &&
    sortedExperts.every((e) => norm(e?.status) === "қате анықтау аяқталды");

  const handleResend = async (exp) => {
    try {
      setResendLoadingId(exp.id);
      const res = await fetch(
        `https://expertise.gov.kz/api/applications/${application.id}/error-review/resend`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({ external_expert_id: exp.id }),
        }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      if (typeof refreshApplication === "function") await refreshApplication(application.id);
      else if (typeof refetch === "function") await refetch();
      else if (typeof onResendSuccess === "function") {
        await onResendSuccess({ applicationId: application.id, expertId: exp.id });
      }
      alert(isKz ? "Сәтті қайтарылды!" : "Успешно возвращено!");
      if (typeof onClose === "function") onClose();
    } catch (e) {

      alert(isKz ? "Қате орын алды" : "Произошла ошибка");
    } finally {
      setResendLoadingId(null);
    }
  };

  return (
    <div className="arm-overlay">
      <div className="arm-modal">
        <header className="arm-header">
          <div>
            <div className="arm-badges">
              <span className="arm-chip arm-chip--neutral">
                {isKz ? "Өтінімді қарау" : "Просмотр заявки"}
              </span>
              <span className={`arm-chip arm-chip--${getTone(application?.status)}`}>
                {statusLabel(application?.status, locale)}
              </span>
            </div>
            <h2 className="arm-title">{application?.title || "—"}</h2>
          </div>

          <div className="arm-header-actions">
            {typeof onOpenErrorReview === "function" && (
              <button className="arm-main-btn arm-main-btn--green" onClick={onOpenErrorReview}>
                <ClipboardList size={17} style={WHITE_ICON} />
                {isKz ? "Қателер" : "Ошибки"}
              </button>
            )}
            {typeof onOpenChat === "function" && (
              <button className="arm-main-btn arm-main-btn--violet" onClick={onOpenChat}>
                <MessageCircle size={17} style={WHITE_ICON} />
                {isKz ? "Құжатты келісу парағы" : "Лист согласования документа"}
              </button>
            )}
            {/* Close — dark background so icon is always visible */}
            <button className="arm-close" onClick={onClose} aria-label="Жабу">
  <span style={{ color: "#fff", fontSize: "22px", lineHeight: 1, fontWeight: 300 }}>×</span>
</button>
          </div>
        </header>

        <main className="arm-body">
          <section className="arm-overview">
            <div className="arm-overview-item">
              <span>{isKz ? "Бас сарапшы" : "Главный эксперт"}</span>
              <strong>{leadExpert ? getName(leadExpert) : "—"}</strong>
            </div>
            <div className="arm-overview-item">
              <span>{isKz ? "Сарапшылар" : "Эксперты"}</span>
              <strong>{sortedExperts.length}</strong>
            </div>
            <div className="arm-overview-item">
              <span>{isKz ? "Аяқтағандар" : "Завершили"}</span>
              <strong>{completedCount}</strong>
            </div>
            <div className="arm-overview-item">
              <span>{isKz ? "Орташа балл" : "Средний балл"}</span>
              <strong>{finalAverage ?? (isKz ? "Есептелмеген" : "Не рассчитан")}</strong>
            </div>
            <div className="arm-overview-item arm-overview-item--wide">
              <span>{isKz ? "Қорытынды" : "Итоговое решение"}</span>
              <strong>{summaryDecision ? safeVerdict(summaryDecision) : "—"}</strong>
            </div>
          </section>

          <section className="arm-section">
            <div className="arm-section-head">
              <h3>{isKz ? "Сарапшылар тізімі" : "Список экспертов"}</h3>
              <p>
                {isKz
                  ? "Әр сарапшының ағымдағы кезеңі, рөлі және файлдары"
                  : "Текущий этап, роль и файлы каждого эксперта"}
              </p>
            </div>

            <div className="arm-expert-list">
              {sortedExperts.map((exp, index) => {
                const lead = isLeadExpert(exp);
                const tone = getTone(exp?.status);

                return (
                  <div className={`arm-expert-row ${lead ? "is-lead" : ""}`} key={exp?.id ?? index}>
                    <div className="arm-expert-main">
                      <div className="arm-expert-index">{index + 1}</div>
                      <div className="arm-expert-info">
                        <div className="arm-expert-name-row">
                          <h4>{getName(exp)}</h4>
                          {lead && (
                            <span className="arm-chip arm-chip--gold">
                              {isKz ? "Бас сарапшы" : "Главный эксперт"}
                            </span>
                          )}
                          <span className={`arm-chip arm-chip--${tone}`}>
                            {safeStatus(exp?.status)}
                          </span>
                        </div>
                        <div className="arm-expert-details">
                          <span><b>{isKz ? "Пән:" : "Предмет:"}</b> {getSubject(exp, locale)}</span>
                          <span><b>{isKz ? "Рөлі:" : "Роль:"}</b> {getRole(exp, locale)}</span>
                          <span><b>{isKz ? "Балл:" : "Балл:"}</b> {getScore(exp, locale)}</span>
                        </div>
                      </div>
                    </div>

                    <div className="arm-expert-files">
                      {exp?.has_mistakes_file && typeof downloadMistakesFile === "function" ? (
                        <button
                          className="arm-file-btn"
                          onClick={() => downloadMistakesFile(application.id, exp.id)}
                          disabled={resendLoadingId === exp.id}
                        >
                          {isKz ? "Қателер файлы" : "Файл ошибок"}
                        </button>
                      ) : (
                        <span className="arm-file-empty">
                          {isKz ? "Қателер файлы жоқ" : "Файла ошибок нет"}
                        </span>
                      )}

                      {exp?.has_decision_file && typeof downloadDecisionFile === "function" ? (
                        <button
                          className="arm-file-btn"
                          onClick={() => downloadDecisionFile(application.id, exp.id)}
                          disabled={resendLoadingId === exp.id}
                        >
                          {isKz ? "Шешім файлы" : "Файл решения"}
                        </button>
                      ) : (
                        <span className="arm-file-empty">
                          {isKz ? "Шешім файлы жоқ" : "Файла решения нет"}
                        </span>
                      )}

                      {exp?.has_mistakes_file && (
                        <button
                          className="arm-file-btn arm-file-btn--danger"
                          onClick={() => handleResend(exp)}
                          disabled={resendLoadingId === exp.id}
                        >
                          {resendLoadingId === exp.id
                            ? isKz ? "Жіберілуде..." : "Отправка..."
                            : isKz ? "Қайтару" : "Вернуть"}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}

              {sortedExperts.length === 0 && (
                <div className="arm-empty">
                  {isKz ? "Сарапшылар табылмады" : "Эксперты не найдены"}
                </div>
              )}
            </div>
          </section>

          {allErrorDetectionFinished && typeof promoteToEvaluation === "function" && (
            <div className="arm-footer-actions">
              <button
                className="arm-action-btn arm-action-btn--primary"
                onClick={() => promoteToEvaluation(application.id)}
              >
                {isKz ? "Бағалауға жіберу" : "Перевести на этап оценки"}
              </button>
            </div>
          )}

          <section className="arm-footer-actions">
            {typeof downloadFinalDecision === "function" && (
              <button
                className="arm-action-btn arm-action-btn--dark"
                onClick={() => downloadFinalDecision(application.id)}
              >
                <FileText size={18} style={WHITE_ICON} />
                {isKz ? "Қорытындыны жүктеу" : "Скачать итог"}
              </button>
            )}
            {typeof setShowUploadModal === "function" && (
              <button
                className="arm-action-btn arm-action-btn--green"
                onClick={() => setShowUploadModal(true)}
              >
                <FileCheck2 size={18} style={WHITE_ICON} />
                {isKz ? "Қорытындыны салу DOCX" : "Загрузить итог DOCX"}
              </button>
            )}
          </section>

          {Array.isArray(finalDocuments) && finalDocuments.length > 0 && (
            <section className="arm-section">
              <div className="arm-section-head">
                <h3>{isKz ? "Финалдық құжаттар" : "Финальные документы"}</h3>
              </div>
              <div className="arm-table-wrap">
                <table className="arm-table">
                  <thead>
                    <tr>
                      <th>{isKz ? "Файл" : "Файл"}</th>
                      <th>{isKz ? "Орташа балл" : "Средний балл"}</th>
                      <th>{isKz ? "Қорытынды" : "Вердикт"}</th>
                      <th>{isKz ? "Күні" : "Дата"}</th>
                      <th>{isKz ? "Күйі" : "Статус"}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {finalDocuments.map((doc) => (
                      <tr key={doc?.id}>
                        <td>
                          {typeof downloadFinalDoc === "function" && (
                            <button className="arm-link-btn" onClick={() => downloadFinalDoc(application.id)}>
                              {doc?.file_path?.split("/").pop() || "DOCX"}
                            </button>
                          )}
                          {doc?.final_attachment && typeof downloadFinalAttachment === "function" && (
                            <button className="arm-link-btn" onClick={() => downloadFinalAttachment(doc.id)}>
                              {doc.final_attachment.split("/").pop()}
                            </button>
                          )}
                        </td>
                        <td>{doc?.average_score ?? "—"}</td>
                        <td>{safeVerdict(doc?.verdict ?? summaryDecision)}</td>
                        <td>{formatDateTime(doc?.created_at)}</td>
                        <td>
                          <span className={`arm-chip arm-chip--${getTone(doc?.status)}`}>
                            {statusLabel(doc?.status, locale)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </main>

        {showUploadModal && (
          <div className="arm-upload-overlay">
            <div className="arm-upload">
              <div className="arm-upload-head">
                <h3>{isKz ? "Финалдық құжатты жүктеу" : "Загрузка финального документа"}</h3>
                <button
                  onClick={() => {
                    setShowUploadModal(false);
                    if (typeof setFinalFile === "function") setFinalFile(null);
                    if (typeof setFinalUploaded === "function") setFinalUploaded(false);
                  }}
                >
                  <span style={{ color: "#fff", fontSize: "20px", lineHeight: 1, fontWeight: 300 }}>×</span>
                </button>
              </div>
              <div className="arm-upload-body">
                <input
                  type="file"
                  accept=".doc,.docx"
                  onChange={(e) => {
                    const file = e?.target?.files?.[0] || null;
                    if (typeof setFinalFile === "function") setFinalFile(file);
                  }}
                />
                {finalFile && (
                  <div className="arm-selected-file">
                    {isKz ? "Таңдалған файл:" : "Выбранный файл:"} <b>{finalFile.name}</b>
                  </div>
                )}
                {typeof uploadFinalAttachment === "function" && (
                  <button
                    className="arm-action-btn arm-action-btn--dark"
                    disabled={!finalFile}
                    onClick={() => uploadFinalAttachment(application.id)}
                  >
                    {isKz ? "Жүктеу" : "Загрузить"}
                  </button>
                )}
                {finalUploaded && typeof handleSendAndGenerate === "function" && (
                  <button
                    className="arm-action-btn arm-action-btn--green"
                    onClick={handleSendAndGenerate}
                    disabled={!!sendLoading}
                  >
                    {sendLoading
                      ? isKz ? "Жіберілуде..." : "Отправка..."
                      : isKz ? "Жіберу және PDF жасау" : "Отправить и сформировать PDF"}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {ChatBoxComponent && chatBoxProps?.isOpen && <ChatBoxComponent {...chatBoxProps} />}
      </div>
    </div>
  );
}